import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Camera, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { MealCaptureModal } from "@/components/public/MealCaptureModal";

interface MealItem {
  id: string;
  meal_name: string;
  meal_type: string;
  scheduled_time: string | null;
  total_calories: number | null;
  day_number: number | null;
  foods: any;
}

interface MealSubmission {
  id: string;
  meal_item_id: string;
  ai_calculated_calories: number | null;
  submitted_at: string;
}

const dayNames: Record<number, string> = {
  1: "Segunda-feira",
  2: "Terça-feira",
  3: "Quarta-feira",
  4: "Quinta-feira",
  5: "Sexta-feira",
  6: "Sábado",
  7: "Domingo"
};

const mealTypeIcons: Record<string, string> = {
  breakfast: "🌅",
  snack: "🍎",
  lunch: "🍽️",
  dinner: "🌙",
  default: "🍴"
};

export default function MealTrackingPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [patientName, setPatientName] = useState("");
  const [mealPlan, setMealPlan] = useState<any>(null);
  const [mealItems, setMealItems] = useState<MealItem[]>([]);
  const [submissions, setSubmissions] = useState<MealSubmission[]>([]);
  const [selectedMeal, setSelectedMeal] = useState<MealItem | null>(null);
  const [selectedDay, setSelectedDay] = useState(() => {
    const jsDay = new Date().getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
    return jsDay === 0 ? 7 : jsDay;    // Converter para 1=Seg, ..., 7=Dom
  });

  useEffect(() => {
    validateAndLoad();
  }, [token]);

  const validateAndLoad = async () => {
    try {
      // Validate token
      const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
        "validate-meal-tracking-token",
        { body: { token } }
      );

      if (tokenError || !tokenData?.valid) {
        toast.error("Link inválido ou expirado");
        setValidToken(false);
        setLoading(false);
        return;
      }

      setValidToken(true);
      setPatientName(tokenData.patient?.full_name || "");
      setMealPlan(tokenData.mealPlan);

      // Use data already loaded by edge function (bypasses RLS)
      setMealItems(tokenData.mealItems || []);
      setSubmissions(tokenData.submissions || []);

      console.log('Loaded from edge function:', tokenData.mealItems?.length, 'items,', tokenData.submissions?.length, 'submissions');

    } catch (error) {
      console.error("Error validating token:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmissionComplete = () => {
    setSelectedMeal(null);
    validateAndLoad();
  };

  const getMealSubmission = (mealItemId: string) => {
    return submissions.find(s => s.meal_item_id === mealItemId);
  };

  const todayMeals = mealItems.filter(m => m.day_number === selectedDay);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4">
        <div className="container max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="rounded-full bg-destructive/10 w-16 h-16 flex items-center justify-center mx-auto">
              <Clock className="h-8 w-8 text-destructive" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Link Inválido</h2>
              <p className="text-muted-foreground">
                Este link de acompanhamento está inválido ou expirado.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4">
      <div className="container max-w-2xl mx-auto space-y-6 pb-8">
        {/* Header */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
          <CardHeader>
            <CardTitle className="text-2xl">
              🍽️ Meu Plano Alimentar
            </CardTitle>
            <p className="text-lg">Olá, {patientName}! 👋</p>
          </CardHeader>
        </Card>

        {/* Day selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {(() => {
                const availableDays = [...new Set(mealItems.map(m => m.day_number))].sort();
                return availableDays.map((day) => (
                  <Button
                    key={day}
                    variant={selectedDay === day ? "default" : "outline"}
                    onClick={() => setSelectedDay(day!)}
                    className="flex-shrink-0"
                  >
                    {dayNames[day!]}
                  </Button>
                ));
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Today's meals */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold px-1">
            📅 {dayNames[selectedDay]}
          </h3>

          {todayMeals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Nenhuma refeição programada para este dia
              </CardContent>
            </Card>
          ) : (
            todayMeals.map((meal) => {
              const submission = getMealSubmission(meal.id);
              const mealIcon = mealTypeIcons[meal.meal_type] || mealTypeIcons.default;

              return (
                <Card
                  key={meal.id}
                  className={submission ? "border-green-500 bg-green-50 dark:bg-green-950/20" : ""}
                >
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-3xl">{mealIcon}</span>
                        <div>
                          <h4 className="font-semibold">{meal.meal_name}</h4>
                          {meal.scheduled_time && (
                            <p className="text-sm text-muted-foreground">
                              {meal.scheduled_time}
                            </p>
                          )}
                          <p className="text-sm font-medium text-primary">
                            {meal.total_calories || 0} kcal previstas
                          </p>
                        </div>
                      </div>
                    </div>

                    {submission ? (
                      <div className="bg-green-100 dark:bg-green-900/20 p-3 rounded-lg space-y-1">
                        <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium">
                          <CheckCircle2 className="h-5 w-5" />
                          <span>Enviado às {new Date(submission.submitted_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        {submission.ai_calculated_calories && (
                          <p className="text-sm">
                            <strong>{submission.ai_calculated_calories} kcal</strong> detectadas pela IA
                          </p>
                        )}
                      </div>
                    ) : (
                      <Button
                        className="w-full gap-2 h-12 text-base"
                        size="lg"
                        onClick={() => setSelectedMeal(meal)}
                      >
                        <Camera className="h-5 w-5" />
                        📷 Enviar Foto
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Info card */}
        <Card className="bg-muted">
          <CardContent className="pt-6 space-y-2 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <span>💡</span>
              <span>
                <strong>Dica:</strong> Fotografe toda a refeição de cima para baixo para uma análise mais precisa das calorias.
              </span>
            </p>
          </CardContent>
        </Card>
      </div>

      {selectedMeal && (
        <MealCaptureModal
          meal={selectedMeal}
          token={token!}
          onClose={() => setSelectedMeal(null)}
          onSuccess={handleSubmissionComplete}
        />
      )}
    </div>
  );
}
